import Cookies from "js-cookie";
import { persistUserSession, updateAuthTokenFromResponse } from "./authSession";
import { appStorage } from "./appStorage";
import { APP_SETUP_STATIC_INVITE_CODE } from "../config/appSetupConfig";

const getApiBase = () => process.env.REACT_APP_API_BASE_URL;

const parseUserFromResponse = (data) => {
  const row = Array.isArray(data?.data) ? data.data[0] : data?.data;
  return row?.user || row || data?.user || null;
};

export const getDeviceName = () => {
  const userAgent = navigator.userAgent || "";
  const platform = navigator.userAgentData?.platform || navigator.platform || "Unknown device";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iPhone/iPad";
  if (/Windows/i.test(userAgent)) return "Windows PC";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "Mac";
  if (/Linux/i.test(userAgent)) return "Linux";
  return platform;
};

export const getClientIpAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) return "";
    const data = await response.json();
    return data?.ip || "";
  } catch {
    return "";
  }
};

export const appSetupSignup = async (formData) => {
  const apiBaseUrl = getApiBase();
  if (!apiBaseUrl) throw new Error("Server configuration error. Please contact support.");

  const [deviceName, ipAddress] = await Promise.all([
    Promise.resolve(getDeviceName()),
    getClientIpAddress(),
  ]);

  const payload = new URLSearchParams({
    first_name: formData.firstName,
    last_name: formData.lastName,
    email: formData.email,
    country_code: formData.countryCode,
    phone_number: formData.phoneNumber,
    password: formData.password,
    deviceType: "Website",
    device_name: deviceName,
    ip_address: ipAddress,
    invite: APP_SETUP_STATIC_INVITE_CODE,
  });

  const response = await fetch(`${apiBaseUrl}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: payload.toString(),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Signup failed");
  }

  appStorage.setItem("email", formData.email);
  appStorage.setItem("phone_number", formData.phoneNumber);
  appStorage.setItem("country_code", formData.countryCode);

  return data;
};

export const resendEmailOtp = async (email) => {
  const apiBaseUrl = getApiBase();
  const response = await fetch(`${apiBaseUrl}/resend-verify-email-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ email }).toString(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Failed to resend email OTP");
  }
  return data;
};

export const verifyEmailOtp = async (email, otp) => {
  const apiBaseUrl = getApiBase();
  const response = await fetch(`${apiBaseUrl}/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ email, otp }).toString(),
  });

  const data = await response.json();
  const status = data.status;
  const message = data.message;
  const user = parseUserFromResponse(data);

  if (status === "0" || status === 0) {
    throw new Error(message || "Invalid email OTP");
  }

  if (user?.token) {
    Cookies.set("auth_token", user.token, { expires: 7, path: "/" });
  }
  updateAuthTokenFromResponse(data, user);
  persistUserSession(user);

  if (Number(user?.payment_status) === 1) {
    appStorage.setItem("choose_plan_required", "0");
    appStorage.setItem("upgrade_plan_required", "0");
  }

  const needsMobileOtp =
    (status === "1" || status === 1) &&
    (user?.sms_verified === 0 || user?.sms_verified === "0") &&
    Boolean(user?.phone_number);

  if (needsMobileOtp) {
    appStorage.setItem("sms_verified", "0");
    appStorage.setItem("verification_type", "3");
  }

  return { status, message, user, needsMobileOtp };
};

export const resendPhoneOtp = async () => {
  const apiBaseUrl = getApiBase();
  const phone = appStorage.getItem("phone_number");
  const code = appStorage.getItem("country_code");
  if (!phone || !code) throw new Error("Phone details not found.");

  const response = await fetch(`${apiBaseUrl}/resend-phone-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      country_code: code,
      phone_number: phone,
      send_via: "sms",
    }).toString(),
  });

  const data = await response.json().catch(() => ({}));
  const status = data.status ?? data.Status;

  if (status === "5" || status === 5) {
    throw new Error(data.message || "Unable to send SMS verification.");
  }

  const ok =
    response.ok &&
    (status === "1" || status === 1 || status === "3" || status === 3 || status == null);

  if (!ok) throw new Error(data.message || "Failed to resend SMS code");
  return data;
};

export const verifyPhoneOtp = async (otp) => {
  const apiBaseUrl = getApiBase();
  const phone = appStorage.getItem("phone_number");
  const code = appStorage.getItem("country_code");
  if (!phone || !code) throw new Error("Phone details not found.");

  const response = await fetch(`${apiBaseUrl}/verify-phone`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      country_code: code,
      phone_number: phone,
      otp,
    }).toString(),
  });

  const data = await response.json();
  const status = data.status ?? data.Status;
  const message = data.message ?? data.Message;
  const user = parseUserFromResponse(data);

  if (status === "0" || status === 0) {
    throw new Error(message || "Invalid mobile OTP");
  }

  const success =
    status === "1" || status === 1 || status === "3" || status === 3;

  if (!success) {
    throw new Error(message || "Invalid mobile OTP");
  }

  updateAuthTokenFromResponse(data, user);
  persistUserSession(user);
  appStorage.setItem("sms_verified", user?.sms_verified?.toString() ?? "1");

  return { status, message, user };
};

const toDDMMYYYY = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const iso = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${String(iso[3]).padStart(2, "0")}/${String(iso[2]).padStart(2, "0")}/${iso[1]}`;
  const slash = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return `${String(slash[1]).padStart(2, "0")}/${String(slash[2]).padStart(2, "0")}/${slash[3]}`;
  return text;
};

const PERSONAL_KEYS = [
  "first_name", "last_name", "full_name", "personal_email_id", "work_email_id", "secondary_email",
  "marital_status", "date_of_birth", "date_of_anniversary", "birth_city_country", "current_city_region_country",
  "country_code_personal", "personal_mobile", "basic_pincode_home",
  "country_code_home", "secondary_mobile", "work_mobile_country_code", "work_mobile",
  "country_code_home_fixed", "home_fixed", "country_code_work_fixed", "work_fixed",
];

export const submitPersonalInfo = async (formData) => {
  const apiBaseUrl = getApiBase();
  const token = Cookies.get("auth_token");
  if (!token) throw new Error("Session expired. Please start again.");

  const payload = new URLSearchParams();
  PERSONAL_KEYS.forEach((k) => payload.append(k, formData[k] ?? ""));
  payload.set("date_of_birth", toDDMMYYYY(formData.date_of_birth));
  payload.set("date_of_anniversary", toDDMMYYYY(formData.date_of_anniversary));

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: `Bearer ${token}`,
  };

  let response = await fetch(`${apiBaseUrl}/insert_personal_info`, {
    method: "POST",
    headers,
    body: payload.toString(),
  });

  let data = await response.json();
  if (data.status !== "1") {
    response = await fetch(`${apiBaseUrl}/update_personal_info`, {
      method: "POST",
      headers,
      body: payload.toString(),
    });
    data = await response.json();
    if (data.status !== "1") {
      throw new Error(data.message || "Failed to save personal info");
    }
  }

  updateAuthTokenFromResponse(data);
  return data;
};

const convertMonthValue = (v) => {
  if (!v) return "";
  if (v.includes("-")) {
    const [y, m] = v.split("-");
    return `${String(m).padStart(2, "0")}/${y}`;
  }
  if (v.includes("/")) {
    const [m, y] = v.split("/");
    return `${String(m).padStart(2, "0")}/${y}`;
  }
  return v;
};

export const submitEducation = async (educationData, isCurrentStudying) => {
  const apiBaseUrl = getApiBase();
  const token = Cookies.get("auth_token");
  if (!token) throw new Error("Session expired. Please start again.");

  const payload = new URLSearchParams();
  payload.set("edu_institution_name", educationData.edu_institution_name || "");
  payload.set("edu_pin_code", educationData.edu_pin_code || educationData.edu_address_pincode || "");
  payload.set("edu_address_pincode", educationData.edu_address_pincode || educationData.edu_pin_code || "");
  payload.set("edu_city_region_country", educationData.edu_city_region_country || "");
  payload.set("edu_address_city", educationData.edu_address_city || "");
  payload.set("edu_address_region", educationData.edu_address_region || "");
  payload.set("edu_address_country", educationData.edu_address_country || "");
  payload.set("course_name", educationData.course_name || "");
  payload.set("edu_start_date", convertMonthValue(educationData.edu_start_date));
  payload.set(
    "edu_end_date",
    isCurrentStudying ? "" : convertMonthValue(educationData.edu_end_date)
  );
  payload.set("education_current_studying", educationData.education_current_studying || "0");

  const response = await fetch(`${apiBaseUrl}/insert_education`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Bearer ${token}`,
    },
    body: payload.toString(),
  });

  const data = await response.json();
  if (data.status !== "1") {
    throw new Error(data.message || "Failed to save education");
  }
  return data;
};

export const registerWaitlistPlatform = async (email, platform) => {
  const apiBaseUrl = getApiBase();
  if (!apiBaseUrl || apiBaseUrl === "undefined") return null;

  const payload = new URLSearchParams({
    email: email.trim().toLowerCase(),
    platform,
    device_type: platform === "android" ? "Android" : "iOS",
  });

  const response = await fetch(`${apiBaseUrl}/waitlist/register`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: payload.toString(),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok && response.status !== 404) {
    throw new Error(data?.message || "Unable to save platform preference.");
  }
  return data;
};
